/**
 * Brand configuration per community. Used by CommunityLanding and CommunityDashboard
 * to render the same UX with different branding, apps and Polaroid Specials.
 */

const PALETTE = {
  bg: '#0E4D2A',
  bgDark: '#083B1F',
  gold: '#D4AF37',
  goldHover: '#E5C158',
  cream: '#F2E8D5',
  muted: '#A8B4A6',
};

const LOGO_URL =
  '/api/static/brand/logo-h0.png';

// Generic Polaroid Specials shared shape (Mercat is shared across communities)
const POLAROID_SPECIALS_GENERIC = [
  {
    key: 'gifts',
    emoji: '🎁',
    title: { ca: 'Regals i Premis', es: 'Regalos y Premios', en: 'Gifts & Prizes', fr: 'Cadeaux & Prix' },
    note: { ca: 'Sorpreses només pels membres', es: 'Sorpresas solo para socios', en: 'Surprises just for members', fr: 'Surprises pour les membres' },
    tape: '#F4D77A',
    tilt: -2.5,
  },
  {
    key: 'market',
    emoji: '🛒',
    title: { ca: 'Mercat', es: 'Mercado', en: 'Marketplace', fr: 'Marché' },
    note: { ca: 'Nous i 2a mà · compartit entre comunitats', es: 'Nuevos y 2ª mano · compartido entre comunidades', en: 'New & 2nd hand · shared across communities', fr: 'Neufs & occasion · partagé' },
    tape: '#E8B4B4',
    tilt: 1.8,
    shared: true,
  },
  {
    key: 'profe',
    emoji: '🧑‍🏫',
    title: { ca: 'Profe, què em passa?', es: 'Profe, ¿qué me pasa?', en: 'Coach, what\'s up?', fr: 'Prof, qu\'est-ce qui m\'arrive ?' },
    note: { ca: 'Consulta personal', es: 'Consulta personal', en: 'Personal consult', fr: 'Consultation perso' },
    tape: '#A8D5BA',
    tilt: -1.2,
  },
  {
    key: 'private',
    emoji: '⛳',
    title: { ca: 'Classes particulars', es: 'Clases particulares', en: 'Private lessons', fr: 'Cours particuliers' },
    note: { ca: 'Un a un, fes una cita', es: 'Uno a uno, pide cita', en: 'One-to-one, book a slot', fr: 'En tête-à-tête' },
    tape: '#B8C5E0',
    tilt: 2.2,
  },
  {
    key: 'gallery',
    emoji: '📷',
    title: { ca: 'Galeria', es: 'Galería', en: 'Gallery', fr: 'Galerie' },
    note: { ca: 'Fotos i records', es: 'Fotos y recuerdos', en: 'Photos & memories', fr: 'Photos & souvenirs' },
    tape: '#C8B4E8',
    tilt: -1.5,
  },
];

// Apps shared across all 3 communities (Rules, GOLFLIX, Mental) — Classes is Nuts-only
const SHARED_APPS = [
  {
    key: 'rules',
    title: 'Rules',
    description: { ca: "L'app per fer els tests oficials de regles i consultar un àrbitre virtual.", es: 'La app para tests oficiales de reglas y consultar un árbitro virtual.', en: 'Official rules tests and virtual referee.', fr: 'Tests officiels de règles et arbitre virtuel.' },
    url: 'https://golf-warmup-game.emergent.host',
  },
  {
    key: 'mental',
    title: 'Mental',
    description: { ca: 'Entrena la teva ment i millora el teu golf.', es: 'Entrena tu mente y mejora tu golf.', en: 'Train your mind and improve your golf.', fr: 'Entraîne ton mental et améliore ton golf.' },
    url: '#mental-sso',
    sso: true,
    requiresEnabled: 'mental',
    // ssoEndpoint is set per-community in the brand configs below
  },
  {
    key: 'golflix',
    title: 'GOLFLIX',
    description: { ca: 'Contingut audiovisual de golf organitzat per categories.', es: 'Contenido audiovisual de golf organizado por categorías.', en: 'Golf audiovisual content by categories.', fr: 'Contenu audiovisuel de golf par catégories.' },
    url: 'https://golf-content-hub-1.emergent.host',
  },
];

// Per-community forum branding (emoji + multilingual labels)
const FORUM_BRANDING = {
  nuts: {
    emoji: '🥜',
    eyebrow: { ca: 'FÒRUM NUTS', es: 'FORO NUTS', en: 'NUTS FORUM', fr: 'FORUM NUTS' },
    title: { ca: 'El fòrum dels Nuts', es: 'El foro de los Nuts', en: 'The Nuts forum', fr: 'Le forum des Nuts' },
    roomSubtitle: { ca: 'Conversa amb els Nuts', es: 'Habla con los Nuts', en: 'Chat with the Nuts', fr: 'Discute avec les Nuts' },
    backLabel: { ca: 'Nuts', es: 'Nuts', en: 'Nuts', fr: 'Nuts' },
  },
  caps: {
    emoji: '🧢',
    eyebrow: { ca: 'FÒRUM CAPS', es: 'FORO CAPS', en: 'CAPS FORUM', fr: 'FORUM CAPS' },
    title: { ca: 'El fòrum dels Caps', es: 'El foro de los Caps', en: 'The Caps forum', fr: 'Le forum des Caps' },
    roomSubtitle: { ca: 'Conversa amb els Caps', es: 'Habla con los Caps', en: 'Chat with the Caps', fr: 'Discute avec les Caps' },
    backLabel: { ca: 'Caps', es: 'Caps', en: 'Caps', fr: 'Caps' },
  },
  shark: {
    emoji: '🦈',
    eyebrow: { ca: 'FÒRUM SHARK', es: 'FORO SHARK', en: 'SHARK FORUM', fr: 'FORUM SHARK' },
    title: { ca: 'El fòrum dels Sharks', es: 'El foro de los Sharks', en: 'The Sharks forum', fr: 'Le forum des Sharks' },
    roomSubtitle: { ca: 'Conversa amb els Sharks', es: 'Habla con los Sharks', en: 'Chat with the Sharks', fr: 'Discute avec les Sharks' },
    backLabel: { ca: 'Shark', es: 'Shark', en: 'Shark', fr: 'Shark' },
  },
  quinta: {
    emoji: '🍺',
    eyebrow: { ca: 'FÒRUM QUINTA', es: 'FORO QUINTA', en: 'QUINTA FORUM', fr: 'FORUM QUINTA' },
    title: { ca: 'El fòrum de la Quinta', es: 'El foro de la Quinta', en: 'The Quinta forum', fr: 'Le forum de la Quinta' },
    roomSubtitle: { ca: 'Conversa amb els Quintos', es: 'Habla con los Quintos', en: 'Chat with the Quintos', fr: 'Discute avec les Quintos' },
    backLabel: { ca: 'Quinta', es: 'Quinta', en: 'Quinta', fr: 'Quinta' },
  },
};

export const COMMUNITY_BRANDS = {
  nuts: {
    community: 'nuts',
    label: '🥜 NUTS · ANDORRA',
    region: 'Andorra & Alt Urgell',
    forumBranding: FORUM_BRANDING.nuts,
    hero: {
      ca: { title: 'Una nova comunitat de golf', subtitle: 'Per nous amants del golf d\'Andorra i l\'Alt Urgell.' },
      es: { title: 'Una nueva comunidad de golf', subtitle: 'Para nuevos amantes del golf de Andorra y el Alto Urgel.' },
      en: { title: 'A new golf community', subtitle: 'For new golf lovers of Andorra and Alt Urgell.' },
      fr: { title: 'Une nouvelle communauté de golf', subtitle: 'Pour les nouveaux amoureux du golf d\'Andorre et Haut Urgell.' },
    },
    apps: [
      {
        key: 'classes',
        title: 'Classes UGOLF Aravell',
        description: { ca: 'Reserva les teves classes col·lectives.', es: 'Reserva tus clases colectivas.', en: 'Book your group classes.', fr: 'Réserve tes cours collectifs.' },
        url: 'https://golfclass-scheduler.emergent.host',
        primary: true,
        sso: true,
        ssoEndpoint: '/api/nuts/sso/classes',
      },
      ...SHARED_APPS.map((a) => (a.key === 'mental' ? { ...a, ssoEndpoint: '/api/nuts/sso/mental' } : a)),
    ],
    specials: POLAROID_SPECIALS_GENERIC,
    palette: PALETTE,
    logoUrl: LOGO_URL,
  },
  caps: {
    community: 'caps',
    label: '🧢 CAPS · LLEIDA',
    region: 'Lleida',
    forumBranding: FORUM_BRANDING.caps,
    hero: {
      ca: { title: 'Caps de Lleida', subtitle: 'La comunitat dels golfistes de Lleida.' },
      es: { title: 'Caps de Lleida', subtitle: 'La comunidad de los golfistas de Lleida.' },
      en: { title: 'Caps de Lleida', subtitle: 'The community of Lleida golfers.' },
      fr: { title: 'Caps de Lleida', subtitle: 'La communauté des golfeurs de Lleida.' },
    },
    // No "classes" entry — Caps don't include group classes
    apps: SHARED_APPS.map((a) => (a.key === 'mental' ? { ...a, ssoEndpoint: '/api/caps/sso/mental' } : a)),
    specials: POLAROID_SPECIALS_GENERIC,
    palette: PALETTE,
    logoUrl: LOGO_URL,
  },
  shark: {
    community: 'shark',
    label: '🦈 SHARK',
    region: 'Amics i experts',
    forumBranding: FORUM_BRANDING.shark,
    hero: {
      ca: { title: 'Shark', subtitle: 'Si hem coincidit jugant a golf, aquest és el teu espai a Forat Zero.' },
      es: { title: 'Shark', subtitle: 'Si hemos coincidido jugando a golf, este es tu espacio en Hoyo Zero.' },
      en: { title: 'Shark', subtitle: 'If we\'ve crossed paths playing golf, this is your space at Hole Zero.' },
      fr: { title: 'Shark', subtitle: 'Si on a joué au golf ensemble, voici ton espace à Trou Zero.' },
    },
    apps: SHARED_APPS.map((a) => (a.key === 'mental' ? { ...a, ssoEndpoint: '/api/shark/sso/mental' } : a)),
    specials: POLAROID_SPECIALS_GENERIC,
    palette: PALETTE,
    logoUrl: LOGO_URL,
  },
  quinta: {
    community: 'quinta',
    label: '🍺 QUINTA',
    region: '5a categoria',
    forumBranding: FORUM_BRANDING.quinta,
    hero: {
      ca: { title: 'Quinta', subtitle: 'La comunitat dels golfistes de 5a categoria. Prenem el pal, prenem la broma.' },
      es: { title: 'Quinta', subtitle: 'La comunidad de los golfistas de 5ª categoría. Tomamos el palo, tomamos el pelo.' },
      en: { title: 'Quinta', subtitle: 'The 5th-category golfers community. We swing hard, laugh harder.' },
      fr: { title: 'Quinta', subtitle: 'La communauté des golfeurs de 5ème catégorie. On joue et on rigole.' },
    },
    // No "classes" — Quinta does NOT include group classes (per user requirement)
    apps: SHARED_APPS.map((a) => (a.key === 'mental' ? { ...a, ssoEndpoint: '/api/quinta/sso/mental' } : a)),
    specials: POLAROID_SPECIALS_GENERIC,
    palette: PALETTE,
    logoUrl: LOGO_URL,
    // Distinctive Quilmes-inspired brand style: cursive script + bright blue
    accentColor: '#3AB5E5',
    scriptFont: '"Pacifico", "Lobster", "Brush Script MT", cursive',
  },
};

export const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];
